import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
} from '@mui/material';
import { Link, Link as RouterLink } from 'react-router-dom';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import Label from '../../components/Label';
import Searchbar from '../../components/Searchbar';
import { useAppDispatch, useAppSelector } from '../../store';
import { findRegion } from '../../store/region/api';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../store/region/regionSlice';
import Loader from '../../components/Loader';
import {
  reset as resetRegion,
  setStatus as setRegionStatus,
} from '../../store/region/regionSlice';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { encryptString } from '../../utils/url';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.region);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findRegion({
          limit,
          skip,
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetRegion());
  }, [search]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setRegionStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <Page title={'Region: List'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'Region'}
          links={[{ name: 'Dashboard', href: '/' }]}
          action={
            <Button
              variant='contained'
              component={RouterLink}
              to={'/region/create'}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              {'New Region'}
            </Button>
          }
        />

        <Card>
          <Searchbar handleSearch={handleSearch} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
              <Table size={'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>{'Label EN'}</TableCell>
                    <TableCell align='center'>{'Label AR'}</TableCell>
                    <TableCell align='center'>{'Country'}</TableCell>
                    <TableCell align='center'>{'Code'}</TableCell>
                    <TableCell align='center'>{'Status'}</TableCell>
                    <TableCell align='center'>{'Action'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((region) => (
                      <TableRow key={region.id}>
                        <TableCell align='center'>{region?.labelEn}</TableCell>
                        <TableCell align='center'>{region?.labelAr}</TableCell>
                        <TableCell align='center'>
                          {region?.country?.labelEn}
                        </TableCell>
                        <TableCell align='center'>{region?.code}</TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={region.activeState ? 'success' : 'error'}
                          >
                            {region.activeState ? 'Active' : 'InActive'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton aria-label='visibility'>
                            <Link
                              to={`/region/edit/${encryptString(
                                region?.id ?? ''
                              )}`}
                              state={{ ...region }}
                              style={{ color: '#808080' }}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </Link>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default Index;
